export const FILTER_ALL = 'ALL';

export const ROLES = {
  VIEWER: 'viewer',
  ADMIN: 'admin',
};

export const DATA_INPUT_TEMPLATE_FALLBACK_NAMES = {
  scope1_fuel: 'scope1-scope2.xlsx',
  scope2_electricity: 'scope1-scope2.xlsx',
  scope3_af_report: 'scope3-af.xlsx',
  scope3_ewaste: 'scope3-weee.xlsx',
};

/* ------------------------------------------------------------------ *
 * emission_factors — scope, category, sub-category                    *
 * ------------------------------------------------------------------ */

export const SCOPE = {
  SCOPE_1: 'SCOPE_1',
  SCOPE_2: 'SCOPE_2',
  SCOPE_3: 'SCOPE_3',
};

export const SCOPE_OPTIONS = [SCOPE.SCOPE_1, SCOPE.SCOPE_2, SCOPE.SCOPE_3];

export const SCOPE_LABEL = {
  [SCOPE.SCOPE_1]: 'Scope 1',
  [SCOPE.SCOPE_2]: 'Scope 2',
  [SCOPE.SCOPE_3]: 'Scope 3',
};

/** Labels are literals, so this list is ready for a Select as-is. */

export const SCOPE_SELECT_OPTIONS = SCOPE_OPTIONS.map((scope) => ({
  value: scope,
  label: SCOPE_LABEL[scope],
}));

/** `short` = the chip next to the scope, `label` = the English grouping. */

export const CATEGORY_META = {
  GASOLINE: { short: 'Gasoline', label: 'Mobile Combustion' },
  ELECTRICITY: { short: 'Electricity', label: 'Purchased Electricity' },
  CAT_1_1: { short: 'Cat 1.1 · Internet services', label: 'Internet services' },
  CAT_1_2: { short: 'Cat 1.2 · Hardware', label: 'Hardware and electronics' },
  CAT_1_3: { short: 'Cat 1.3 · Legal and finance', label: 'Legal and financial services' },
  CAT_1_4: { short: 'Cat 1.4 · Events', label: 'Events and meetings' },
  CAT_1_5: { short: 'Cat 1.5 · Advertising', label: 'Advertising and marketing' },
  CAT_1_6: { short: 'Cat 1.6 · Training', label: 'Training services' },
  CAT_1_7: { short: 'Cat 1.7 · Office administration', label: 'Office administration' },
  CAT_1_8: { short: 'Cat 1.8 · Worker dispatching', label: 'Worker dispatching' },
  CAT_1_9: { short: 'Cat 1.9 · Intra-group outsourcing', label: 'Intra-group outsourcing' },
  CAT_2: { short: 'Cat 2', label: 'Capital Goods' },
  CAT_3: { short: 'Cat 3', label: 'Fuel & Energy Related Activities' },
  CAT_5: { short: 'Cat 5', label: 'Waste Generated in Operations' },
  CAT_6: { short: 'Cat 6', label: 'Business Travel' },
  CAT_7: { short: 'Cat 7', label: 'Employee Commuting' },
  CAT_8: { short: 'Cat 8', label: 'Upstream Leased Assets' },
  CONTRIBUTION: { short: 'Contribution projects', label: 'Contribution projects' },
  OFFSET: { short: 'Carbon offset programs', label: 'Carbon offset programs' },
  POLICY: { short: 'General policy & behavior', label: 'General policy & behavior' },
};

export const CATEGORY_OPTIONS = Object.keys(CATEGORY_META);

/** A category belongs to exactly one scope, so forms narrow the list. */

export const CATEGORIES_BY_SCOPE = {
  [SCOPE.SCOPE_1]: ['GASOLINE'],
  [SCOPE.SCOPE_2]: ['ELECTRICITY'],
  [SCOPE.SCOPE_3]: CATEGORY_OPTIONS.filter((c) => c.startsWith('CAT_')),
};

export const SUB_CATEGORY_OPTIONS = [
  'BURNABLE',
  'PLASTIC',
  'CAN',
  'PET',
  'BOTTLE',
  'WEEE',
  'TAXI',
  'BUS',
  'RAIL',
  'FLIGHT_DOM',
  'FLIGHT_INT',
  'HOTEL',
  'OTHER',
];

/** Only Cat 5 / 6 / 7 carry a sub-category (§3.3). */

export const SUB_CATEGORIES_BY_CATEGORY = {
  CAT_5: ['BURNABLE', 'PLASTIC', 'CAN', 'PET', 'BOTTLE', 'WEEE'],
  CAT_6: ['TAXI', 'BUS', 'RAIL', 'FLIGHT_DOM', 'FLIGHT_INT', 'HOTEL', 'OTHER'],
  CAT_7: ['BUS', 'RAIL'],
};

export const SUB_CATEGORY_LABELS_BY_CATEGORY = {
  CAT_5: {
    BURNABLE: 'Burnable waste',
    PLASTIC: 'Plastic waste',
    CAN: 'Can waste',
    PET: 'PET bottle waste',
    BOTTLE: 'Bottle waste',
    WEEE: 'Waste Electrical and Electronic Equipment',
  },
  CAT_6: {
    TAXI: 'Travelling (Taxi)',
    BUS: 'Travelling (Bus)',
    RAIL: 'Passenger Rail',
    FLIGHT_DOM: 'Travelling (Plane) Domestic',
    FLIGHT_INT: 'Travelling (Plane) International',
    HOTEL: 'Lodging (Hotel)',
    OTHER: 'Other travel and transportation services',
  },
  CAT_7: {
    BUS: 'Company Bus Commuting',
    RAIL: 'Passenger Rail',
  },
};

/** §3.4 `ef_source_files.source_type`. */

export const SOURCE_TYPE = {
  DB_V3: 'DB_V3',
  DENKI_R08: 'DENKI_R08',
};

/** EF sync official source types (`source_types` request field). */

export const EF_SYNC_SOURCE_TYPES = ['DB_V3', 'DENKI'];

/** EF sync source error token for an official source not published yet. */

export const EF_SYNC_SOURCE_NOT_AVAILABLE = 'SOURCE_NOT_AVAILABLE_FOR_YEAR';

/** A factor stays ACTIVE until another version supersedes it (rule EF2/EF3). */

export const EF_STATUS = {
  ACTIVE: 'ACTIVE',
  SUPERSEDED: 'SUPERSEDED',
};

/** For the `Status` column filter — pass through `useTranslatedOptions`. */

export const EF_STATUS_OPTIONS = [
  { value: EF_STATUS.ACTIVE, label: 'Active' },
  { value: EF_STATUS.SUPERSEDED, label: 'Superseded' },
];

/* ------------------------------------------------------------------ *
 * location_master                                                      *
 * ------------------------------------------------------------------ */

/** Decides the scope of Cat 3 and Cat 8. */

export const LOCATION_TYPE = {
  OFFICE: 'OFFICE',
  NON_OFFICE: 'NON_OFFICE',
};

export const LOCATION_TYPE_OPTIONS = [
  { value: LOCATION_TYPE.OFFICE, label: 'Office' },
  { value: LOCATION_TYPE.NON_OFFICE, label: 'Non-office' },
];

/**
 * §5.4 ELECTRICITY_MENU. The Japanese label is printed on the report line
 * `メニュー：…`; it is display-only and never used to look up a factor.
 */

export const ELECTRICITY_MENU = {
  CORPORATE_AVERAGE: 'CORPORATE_AVERAGE',
  NON_FOSSIL_SOLAR: 'NON_FOSSIL_SOLAR',
  NON_FOSSIL_WIND: 'NON_FOSSIL_WIND',
  NOT_SPECIFIED: 'NOT_SPECIFIED',
};

/** Labels are already Japanese literals, so they never go through `t()`. */

export const ELECTRICITY_MENU_OPTIONS = [
  {
    value: ELECTRICITY_MENU.CORPORATE_AVERAGE,
    label: '(参考値)事業者全体',
  },
  {
    value: ELECTRICITY_MENU.NON_FOSSIL_SOLAR,
    label: '非化石電源（太陽光）',
  },
  {
    value: ELECTRICITY_MENU.NON_FOSSIL_WIND,
    label: '非化石電源（風力）',
  },
  { value: ELECTRICITY_MENU.NOT_SPECIFIED, label: '—' },
];

export const ELECTRICITY_MENU_LABEL = ELECTRICITY_MENU_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {}
);

/* ------------------------------------------------------------------ *
 * Generic record status (`is_active` on every master collection)       *
 * ------------------------------------------------------------------ */

export const RECORD_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
};

/** For the `Status` column filter — pass through `useTranslatedOptions`. */

export const RECORD_STATUS_OPTIONS = [
  { value: RECORD_STATUS.ACTIVE, label: 'Active' },
  { value: RECORD_STATUS.INACTIVE, label: 'Inactive' },
];

/** For the `Green Energy` column filter — pass through `useTranslatedOptions`. */

export const GREEN_ENERGY_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

/** Boolean-valued options for the `is_active` dropdown in master forms. */

export const ACTIVE_FLAG_OPTIONS = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
];

/**
 * Scope 3 AF report — multi-file snapshot import
 */
export const SCOPE3_AF_ERROR_TOKENS = Object.freeze({
  SNAPSHOT_CHANGED: 'SCOPE3_AF_SNAPSHOT_CHANGED',
  SNAPSHOT_UNCHANGED: 'SCOPE3_AF_SNAPSHOT_UNCHANGED',
  CONFIRM_INVALID: 'SCOPE3_AF_CONFIRM_INVALID',
  BUSY: 'SCOPE3_AF_BUSY',
  SNAPSHOT_UNAVAILABLE: 'SCOPE3_AF_SNAPSHOT_UNAVAILABLE',
});

/** Client-facing message token echoed by the assignment mapping-refresh API. */

export const SCOPE2_MAPPING_REFRESH_FAILED = 'SCOPE2_MAPPING_REFRESH_FAILED';
